/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import { CertModel, L7SSLCert, L7VirtualServiceModel, SSLCipherList } from '../../models';
import { CertsApiService, SSLCiphersApiService } from '../../http';
import {
    ChangeDetectorRef,
    Component,
    ComponentRef,
    EventEmitter,
    Input,
    Output,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { CipherListComponent } from '../cipherList/cipherList.component';
import { ErrorResponseModel } from '../../../../../core/models';
import { FormBuilder, FormGroup } from '@angular/forms';
import { LoadingService, LogService, ModalService, RbacService } from '../../../../../core/services';
import { SSLCertificateComponent } from '../../../pages/clusterConfiguration/sslCertificate';
import { WizardService } from '../../services/components';
import { forkJoin, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-lb-ssl-offload-options',
    templateUrl: './sslOffloadOptions.component.html',
    styleUrls: ['./sslOffloadOptions.component.scss'],
})

export class SSLOffloadOptionsComponent extends BaseComponent {
    /**
     * Reference to embedded cipher lists container.
     */
    @ViewChild('cipherListContainer', { read: ViewContainerRef, static: true })
    public cipherListContainer: any;

    /**
     * Reference to add/edit certs container.
     */
    @ViewChild('addEditCertsContainer', { read: ViewContainerRef, static: true })
    public addEditCertsContainer: any;

    /**
     * L7Vip model to pass in.
     */
    @Input() public L7VIPModel: L7VirtualServiceModel;

    /**
     * Incoming certs for the table.
     * @param SSLCerts
     * @constructor
     */
    @Input() set SSLCerts(SSLCerts: L7SSLCert[]) {
        this._SSLCerts = SSLCerts;
        this.populateSSLRows();
    }

    /**
     * Get minimum ssl version from Frontend Services.
     */
    @Input() set minCipherSecVersion(version: string) {
        this._minCipherSecVersion = version;
        if (this.isCipherPageVisible) {
            this.updateCipherListComponent();
        }
    }

    /**
     * Get maximum ssl version from Frontend Services.
     */
    @Input() set maxCipherSecVersion(version: string) {
        this._maxCipherSecVersion = version;
        if (this.isCipherPageVisible) {
            this.updateCipherListComponent();
        }
    }

    /**
     * Input used for external validation from another component.
     * @param validation
     */
    @Input() set validationError(validation: ErrorResponseModel) {
        this._externalValidationError = validation;
        if (this._externalValidationError && this._externalValidationError.message) {
            const certs = this.certsForm.get('certs');
            certs.markAsTouched();
            certs.setErrors({ 'responseError': this._externalValidationError.message });
        }
    }

    /**
     * Incoming cert ID for pre-selection in table.
     */
    @Input() set selectedKey(key: string) {
        if (key?.length) {
            this._setSelectedKey = this.removeTestFromCertId(key[0]['certificateId']);
        }
    }

    /**
     * Index for when used with manageServices for id uniqueness.
     */
    @Input() public templateIndex: number = 0;

    /**
     * Determines how we structure the component in and out of the wizard.
     */
    @Input() public componentEmbedded: boolean = false;

    /**
     * Sends the ssl array out of the component.
     */
    @Output() public sslCerts: EventEmitter<any[]> = new EventEmitter<any[]>();

    /**
     * Emit event to open manage cipher list page.
     */
    @Output() public openManageCipherLists: EventEmitter<any> = new EventEmitter<any>();

    /**
     * Certificates form.
     */
    public certsForm: FormGroup;

    /**
     * Main table rows shown in wizard.
     */
    public sslTableRows: any[] = [];

    /**
     * Temporary list of table objects used for searching (Table ONLY)
     * @type {any[]}
     */
    public tempRows: any[] = [];

    /**
     * Table selected row used when loading in edit mode, highlights selected table row.
     */
    public selectedRow: any = [];

    /**
     * An array containing certificate models of certs currently available on the LB
     * @var SSLCertificates
     */
    public SSLCertificates: CertModel[] = [];

    /**
     * @var userCipherLists
     * The array containing the cipher lists available to the userManagement
     */
    public userCipherLists: SSLCipherList[] = [];

    /**
     * @var certsInUse
     * This is a structure containing the ids of the certificates that are in use.
     * and as a result should be removed/disabled from sslCertificates html dropdown
     */
    public certsInUse: string[] = [];

    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown = {
        certificates: [],
        ciphers: []
    };

    /**
     * Store visibility state of cipher list page.
     */
    public isCipherPageVisible: boolean = false;

    /**
     * Visibility flag for add cert page.
     */
    public isCertPageVisible: boolean = false;

    /**
     * Visibility flag for edit cert page.
     */
    public isEditCertPageVisible: boolean = false;

    /**
     * Visibility flag for add/edit certificates page.
     */
    public isAddEditCertsPageVisible: boolean = false;

    /**
     * Store the permitted certs for the user to add.
     */
    public usableCerts: any[] = [];

    /**
     * Store the data for editing a certificate.
     */
    public editData: any = {};

    /**
     * Store permissions lookup.
     */
    public permissionsLookup: any = {};

    /**
     * Used when adding or editing to store the ssl data.
     * @private
     */
    private _SSLCerts: L7SSLCert[] = [];

    /**
     * Store any external validation errors.
     */
    private _externalValidationError: ErrorResponseModel;

    /**
     * Store selected row key
     * @private
     */
    private _setSelectedKey: string;

    /**
     * Store min cipher sec version
     * @private
     */
    private _minCipherSecVersion: string;

    /**
     * Store max cipher sec version
     * @private
     */
    private _maxCipherSecVersion: string;

    /**
     * Store component reference.
     * @private
     */
    private componentReference: ComponentRef<any>;

    constructor(
        public modalService: ModalService,
        private cdr: ChangeDetectorRef,
        private certsApiService: CertsApiService,
        private formBuilder: FormBuilder,
        private loadingService: LoadingService,
        private logService: LogService,
        private rbacService: RbacService,
        private userCipherListApi: SSLCiphersApiService,
        private wizardService: WizardService
    ) {
        super();
        this.setupForm();
        this.initLoading();
        this.registerButtons();
    }

    /**
     * Get the current headers added to this service.
     * Set up the view.
     */
    get SSLCerts() {
        return this._SSLCerts;
    }

    get selectedKey() {
        return this._setSelectedKey;
    }

    /**
     * Get min/max cipher security versions from the parent.
     */
    get minCipherSecVersion() {
        return this._minCipherSecVersion;
    }

    get maxCipherSecVersion() {
        return this._maxCipherSecVersion;
    }

    /**
     * Check if there is external validation and if it applies to this cert.
     * @param certId
     */
    public checkCertValidation(certId: string): boolean {
        return this._externalValidationError && this._externalValidationError.value === certId;
    }

    /**
     * If we want to add/create a new cipher list and open the modal
     */
    public openManageCipherListsPage(): void {
        this.isCipherPageVisible = true;
        this.wizardService.disableNavigation = true;
        this.setUpCipherListsComponent();
    }

    /**
     * Toggle certificates page visibility.
     * @param visibility
     */
    public toggleCertPage(visibility: boolean): void {
        this.isCertPageVisible = visibility;
        this.wizardService.disableNavigation = visibility;
    }

    /**
     * Toggle edit certificate page visibility.
     * @param visibility
     * @param row
     */
    public toggleEditCertPage(visibility: boolean, row?: any): void {
        if (row) {
            this.editData = {
                editId: row.id,
                name: row.name,
                cipher: row.cipherId
            };
        }
        this.isEditCertPageVisible = visibility;
        this.wizardService.disableNavigation = visibility;
        this.loadingService.loadingActionDotsEmitter.next(false);
    }

    /**
     * Delete SSL cert by row.
     * @param row
     */
    public deleteAnSSLTermination(row: any): void {
        const certToRemove = this.findACertById(row.id);
        const certToRemoveIndex = this.SSLCerts.indexOf(certToRemove);

        if (this.SSLCerts && this.SSLCerts.length === 1) {
            this.confirmDeleteSSLTermination(certToRemoveIndex);
        } else {
            this.SSLCerts.splice(certToRemoveIndex);
            this.populateSSLRows();
            this.sslCerts.emit(this.SSLCerts);
        }
    }

    /**
     * Confirm deletion of certificate.
     * @param certToRemoveIndex
     * @private
     */
    private confirmDeleteSSLTermination(certToRemoveIndex: number): void {
        const confirmationObject = {
            title: 'ssl_offload_remove_ssl_cert_association',
            message: 'ssl_offload_remove_ssl_cert_association_message',
            callBack: this.removeSSLCert.bind(this, certToRemoveIndex),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Remove SSL cert from table.
     * @param certToRemoveIndex
     * @private
     */
    private removeSSLCert(certToRemoveIndex: number): Observable<any> {
        return new Observable((observer) => {
            this.SSLCerts.splice(certToRemoveIndex);
            this.sslCerts.emit(this.SSLCerts);
            observer.next();
            observer.complete();
        });
    }

    /**
     * Add certs to table.
     * @param $event
     */
    public addCertsToTable($event: any): void {
        this.SSLCerts = $event;
        this.sslCerts.emit(this.SSLCerts);
        this.isCertPageVisible = false;
        this.wizardService.disableNavigation = false;
        this.populateSSLRows();
    }

    /**
     * Get updated certs from edit cert component.
     * @param event
     */
    public getUpdatedCerts(event: any): void {
        this.SSLCerts = event;
        this.toggleEditCertPage(false);
        this.populateSSLRows();
        this.sslCerts.emit(this.SSLCerts);
    }

    /**
     * Go to certs page to create a new certificate.
     */
    public createCertificate(): void {
        this.isAddEditCertsPageVisible = true;
        this.wizardService.disableNavigation = true;
        this.setUpAddEditCertsPage();
    }

    /**
     * Setup certs form for external validation e.g. from the wizard.
     * @private
     */
    private setupForm(): void {
        this.certsForm = this.formBuilder.group({
            certs: [null]
        });
    }

    /**
     * Set up the component factory.
     * @private
     */
    private setUpCipherListsComponent(): void {
        const cipherList = this.cipherListContainer.createComponent(CipherListComponent);
        cipherList.instance.componentEmbedded = true;
        cipherList.instance.minCipherSecurityVersion = this.minCipherSecVersion;
        cipherList.instance.maxCipherSecurityVersion = this.maxCipherSecVersion;
        cipherList.instance.callBack = this.cancelCipherListView.bind(this);

        this.componentReference = cipherList;
        this.cdr.detectChanges();
    }

    /**
     * Update cipher list component security options.
     * @private
     */
    private updateCipherListComponent(): void {
        this.componentReference.setInput('minCipherSecurityVersion', this.minCipherSecVersion);
        this.componentReference.setInput('maxCipherSecurityVersion', this.maxCipherSecVersion);
    }

    /**
     * Cancel cipher list view.
     * @private
     */
    private cancelCipherListView(): void {
        this.componentReference.destroy();
        this.isCipherPageVisible = false;
        this.wizardService.disableNavigation = false;
        this.getUpdatedCipherLists();
    }

    /**
     * Get updated cipher lists.
     * @private
     */
    private getUpdatedCipherLists(): void {
        this.userCipherListApi.getAllCipherLists()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (cipherLists: SSLCipherList[]): void => {
                    this.userCipherLists = cipherLists;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'SSLOffloadOptionsComponent: getUpdatedCipherLists() - failed to get cipher lists',
                        error.message);
                }
            });
    }

    /**
     * Set up add/edit certificates page.
     * @private
     */
    private setUpAddEditCertsPage(): void {
        const addEditCerts = this.addEditCertsContainer.createComponent(SSLCertificateComponent);
        addEditCerts.instance.componentEmbedded = true;
        addEditCerts.instance.callBack = this.cancelAddEditCertsView.bind(this);

        this.componentReference = addEditCerts;
        this.cdr.detectChanges();
    }

    /**
     * Cancel cipher list view.
     * @private
     */
    private cancelAddEditCertsView(): void {
        this.componentReference.destroy();
        this.isAddEditCertsPageVisible = false;
        this.wizardService.disableNavigation = false;
        this.updateCertificatesList();
    }

    /**
     * Get updated cipher lists.
     * @private
     */
    private updateCertificatesList(): void {
        this.certsApiService.getCerts()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (certs: CertModel[]): void => {
                    this.SSLCertificates = certs;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'SSLOffloadOptionsComponent: updateCertificatesList() failed to fetch certs', error.message);
                }
            });
    }

    /**
     * Get the certificates and cipher lists from API and set class variables
     * Once set, set up the dropdowns.
     * @private
     */
    private initLoading(): void {
        forkJoin([
            this.certsApiService.getCerts(),
            this.userCipherListApi.getAllCipherLists()
        ])
            .pipe(
                takeUntil(this.destroyed$)
            )
            .subscribe({
                next: (dataGroups: any): void => {
                    this.SSLCertificates = dataGroups[0];
                    this.userCipherLists = dataGroups[1];

                    this.setUsableCerts();

                    this.populateSSLRows();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'SSLOffloadOptionsComponent:initLoading - failed to fetch data ', error.message);
                }
            });
    }

    /**
     * Set the compatible certificates.
     * @private
     */
    private setUsableCerts(): void {
        if (this.SSLCertificates.length) {
            for (const certificate of this.SSLCertificates) {
                if (certificate['type'] !== 'CSR') {
                    this.usableCerts.push(certificate);
                }
            }
        }
    }

    /**
     * Locate a certificate in the certs array by its ID
     * @param id
     * @private
     */
    private findACertById(id: string): L7SSLCert {
        return this.SSLCerts.find(i => i.certificateId === id);
    }

    /**
     * Populate table rows for displaying in the table
     * @private
     */
    private populateSSLRows(): void {
        // Reset our rows variable
        const rows = [];
        this.certsInUse = [];

        if (this.SSLCerts?.length) {
            // Populate our rows with group data
            for (const cert of this.SSLCerts) {
                this.certsInUse.push(this.removeTestFromCertId(cert.certificateId));
                const { name, validTo } = this.getCertObjectById(this.removeTestFromCertId(cert.certificateId));
                const cipherDisplayName = this.getCipherDisplayName(cert.cipherListId);
                rows.push({
                    id: this.removeTestFromCertId(cert.certificateId),
                    name,
                    cipher: cipherDisplayName,
                    cipherId: cert.cipherListId,
                    validTo
                });
            }

            this.sslTableRows = [...rows];
            this.tempRows = [...rows];
        }
    }

    /**
     * Remove test from ID, this is for testing purposes and will need to be removed.
     * @param id
     * @private
     */
    private removeTestFromCertId(id: string): string {
        return id.includes('test') ? id.replace('test', '') : id;
    }

    /**
     * Get certificate by id from the SSL certificates array.
     * @param id
     */
    private getCertObjectById(id: string): any {
        if (this.SSLCertificates == null) {
            return {};
        }
        const certObject: CertModel = this.SSLCertificates.find(i => i.id === id);
        if (certObject == null) {
            return {};
        } else {
            return {
                name: certObject.name,
                validTo: certObject.validTo
            };
        }
    }

    /**
     * Gets cipher display name
     * @param cipherId
     * @private
     */
    private getCipherDisplayName(cipherId: string): string {
        const displayObject = this.userCipherLists.filter(item => item.id === cipherId);
        if (displayObject && displayObject.length) {
            return displayObject[0].name;
        }
    }

    /**
     * Register buttons.
     * @private
     */
    private registerButtons(): void {
        const buttonReferences = {
            'ssl-offload-select-cert': [
                { path: '/certificates/certs', methods: ['GET'] },
                { path: '/ciphers', methods: ['GET'] },
                { path: '/ciphers/lists', methods: ['GET'] },
            ],
            'ssl-offload-create-cert': [
                { path: '/certificates/certs', methods: ['GET'] },
                { path: '/certificates/csrs', methods: ['GET'] },
            ],
            'ssl-offload-edit-cert': [
                { path: '/certificates/certs', methods: ['GET'] },
                { path: '/ciphers/lists', methods: ['GET'] },
            ],
            'ssl-offload-manage-ciphers': [
                { path: '/ciphers/lists', methods: ['GET'] },
            ],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReferences);
    }
}
